/**
 * captureScreen.ts – Viewport capture utility for AI visual context
 *
 * Uses html2canvas (dynamically imported) to capture the full browser viewport
 * including all DOM elements and WebGL canvases (e.g. Three.js).
 *
 * Images are queued as "pending screenshots" that the tool-calling layer
 * consumes and sends to the AI model as multimodal image content.
 */

// ── Pending screenshots queue ─────────────────────────────────────────────────

export interface PendingScreenshot {
  base64: string; // raw base64 (no data:… prefix)
  mimeType: string;
}

let _pendingScreenshots: PendingScreenshot[] = [];

/**
 * Consume and clear all pending screenshots.
 * Called by the tool-calling layer after evaluate() returns.
 */
export function consumePendingScreenshots(): PendingScreenshot[] {
  const screenshots = _pendingScreenshots;
  _pendingScreenshots = [];
  return screenshots;
}

// ── Configuration ─────────────────────────────────────────────────────────────

const MAX_WIDTH = 1280;
const JPEG_QUALITY = 0.7;

// ── Main ──────────────────────────────────────────────────────────────────────

/**
 * Captures the current browser viewport as a JPEG image.
 *
 * - Handles WebGL canvases (e.g. Three.js) by pre-capturing them with
 *   toDataURL() before html2canvas processes the DOM clone (otherwise they
 *   appear blank without preserveDrawingBuffer).
 * - Scales down to max 1280 px width to keep the payload reasonable for AI.
 * - Stores the result in a pending queue; the AI tool layer attaches the
 *   image automatically to the model-facing tool result.
 *
 * @returns A short description string.  The actual image is queued internally
 *          and sent to the AI model as an image content part.
 */
export async function captureScreen(): Promise<string> {
  // Dynamic import – html2canvas is only loaded when actually needed
  const html2canvas = (await import("html2canvas")).default;

  // ── Pre-capture WebGL canvases ────────────────────────────────────────────
  const allCanvases = Array.from(document.querySelectorAll("canvas"));
  const canvasImages = new Map<HTMLCanvasElement, string>();
  for (const canvas of allCanvases) {
    try {
      const gl =
        (canvas as any).__webglContext ??
        canvas.getContext("webgl2") ??
        canvas.getContext("webgl");
      if (gl) {
        // Read pixels while the framebuffer still holds the last frame
        canvasImages.set(canvas, canvas.toDataURL("image/png"));
      }
    } catch {
      // tainted / cross-origin canvas – skip
    }
  }

  // ── html2canvas capture ───────────────────────────────────────────────────
  const capturedCanvas = await html2canvas(document.body, {
    useCORS: true,
    allowTaint: true,
    width: window.innerWidth,
    height: window.innerHeight,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    onclone: (clonedDoc: Document) => {
      // Replace blank WebGL canvases with their pre-captured images
      const clonedCanvases = Array.from(clonedDoc.querySelectorAll("canvas"));
      for (let i = 0; i < clonedCanvases.length && i < allCanvases.length; i++) {
        const dataUrl = canvasImages.get(allCanvases[i]);
        if (!dataUrl) continue;
        const img = clonedDoc.createElement("img");
        img.src = dataUrl;
        const origStyle = window.getComputedStyle(allCanvases[i]);
        img.style.width = origStyle.width;
        img.style.height = origStyle.height;
        img.style.position = origStyle.position;
        img.style.top = origStyle.top;
        img.style.left = origStyle.left;
        clonedCanvases[i].parentNode?.replaceChild(img, clonedCanvases[i]);
      }
    },
  });

  // ── Scale down if needed ──────────────────────────────────────────────────
  let outputCanvas: HTMLCanvasElement = capturedCanvas;
  if (capturedCanvas.width > MAX_WIDTH) {
    const scale = MAX_WIDTH / capturedCanvas.width;
    const scaled = document.createElement("canvas");
    scaled.width = MAX_WIDTH;
    scaled.height = Math.round(capturedCanvas.height * scale);
    const ctx = scaled.getContext("2d")!;
    ctx.drawImage(capturedCanvas, 0, 0, scaled.width, scaled.height);
    outputCanvas = scaled;
  }

  // ── Encode as JPEG and queue ──────────────────────────────────────────────
  const dataUrl = outputCanvas.toDataURL("image/jpeg", JPEG_QUALITY);
  const base64 = dataUrl.replace(/^data:image\/jpeg;base64,/, "");

  _pendingScreenshots.push({ base64, mimeType: "image/jpeg" });

  const sizeKB = Math.round((base64.length * 3) / 4 / 1024);
  return (
    `[Screenshot captured (${outputCanvas.width}×${outputCanvas.height}px, ~${sizeKB} KB). ` +
    `The image is automatically attached to this tool result for your visual analysis.]`
  );
}

